import { fetchApi } from "@/api/fetchApi";
import {
  BankLinkSession,
  ImportBankAccountsRequest,
  ImportBankAccountsResponse,
  BankConnection,
  ListBankConnectionsResponse,
  ReconnectBankResponse,
} from "@/generated/backend/v1/bank_link";

/** POST /api/bank-link/session: a Flinks Connect iframe URL with a fresh authorize token. */
export function createFlinksSession(): Promise<BankLinkSession> {
  return fetchApi("/bank-link/session", { method: "POST", response: BankLinkSession });
}

/**
 * POST /api/bank-link/import: the balances of the login the user just connected, for `ownerUuid`.
 * The backend keeps the connection; each account's `entityUuid` is the uuid its draft item must use.
 */
export function importFlinksAccounts(
  loginId: string,
  ownerUuid: string,
): Promise<ImportBankAccountsResponse> {
  return fetchApi("/bank-link/import", {
    body: ImportBankAccountsRequest.create({ loginId, ownerUuid }),
    request: ImportBankAccountsRequest,
    response: ImportBankAccountsResponse,
  });
}

/**
 * The login id from a Flinks Connect `message` event, when it's the one sent on success
 * (`{ step: "REDIRECT", url: "...?loginId=...&institution=..." }`).
 */
export function loginIdFromFlinksMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return undefined;
  const { step, url } = data as { step?: unknown; url?: unknown };
  if (step !== "REDIRECT" || typeof url !== "string") return undefined;
  try {
    return new URL(url, "https://flinks.invalid").searchParams.get("loginId") || undefined;
  } catch {
    return undefined;
  }
}

/** GET /api/bank-link/connections: the user's banks and the balances waiting for the next check-in. */
export function listFlinksConnections(): Promise<ListBankConnectionsResponse> {
  return fetchApi("/bank-link/connections", { response: ListBankConnectionsResponse });
}

/** DELETE /api/bank-link/connections/:id: disconnects the bank (at Flinks too). */
export async function removeFlinksConnection(id: string): Promise<void> {
  await fetchApi(`/bank-link/connections/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * POST /api/bank-link/connections/:id/reconnect: a live read of the bank. Either done (`connection`),
 * or the bank needs the user: open `iframeUrl`, then call `refreshFlinksConnection`.
 */
export function reconnectFlinksConnection(id: string): Promise<ReconnectBankResponse> {
  return fetchApi(`/bank-link/connections/${encodeURIComponent(id)}/reconnect`, {
    method: "POST",
    response: ReconnectBankResponse,
  });
}

/** POST /api/bank-link/connections/:id/refresh: after the user answered their bank in Flinks Connect. */
export function refreshFlinksConnection(id: string): Promise<BankConnection> {
  return fetchApi(`/bank-link/connections/${encodeURIComponent(id)}/refresh`, {
    method: "POST",
    response: BankConnection,
  });
}
